import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import {
  Box, Grid, Card, CardContent, Typography, Button, TextField, MenuItem,
  InputAdornment, IconButton, LinearProgress,
  Avatar, Divider, Select, FormControl, CircularProgress,
  Dialog, DialogContent, DialogTitle,
} from '@mui/material';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import BusinessIcon from '@mui/icons-material/Business';

import {
  getSiteAssignmentList,
  addSiteAssignment,
  deleteSiteAssignment,
  getCompany,
  getSiteData,
  getEmployee,
} from '../api';

// ─── Month / Year options ─────────────────────────────────────────────────────

const MONTH_OPTIONS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' },
  { value: 3, label: 'March' },   { value: 4, label: 'April' },
  { value: 5, label: 'May' },     { value: 6, label: 'June' },
  { value: 7, label: 'July' },    { value: 8, label: 'August' },
  { value: 9, label: 'September' },{ value: 10, label: 'October' },
  { value: 11, label: 'November' },{ value: 12, label: 'December' },
];

const YEAR_OPTIONS = Array.from({ length: 11 }, (_, i) => 2026 + i);

const nowDate = new Date();
const DEFAULT_MONTH = nowDate.getMonth() + 1;
const DEFAULT_YEAR = nowDate.getFullYear() < 2026 ? 2026 : Math.min(nowDate.getFullYear(), 2036);

// ─── Data helpers ─────────────────────────────────────────────────────────────

const computeStatus = (completed, target, month, year) => {
  if (target === 0) return 'On Track';

  const today = new Date();
  const totalDays = new Date(year, month, 0).getDate();

  if (year > today.getFullYear() || (year === today.getFullYear() && month > today.getMonth() + 1))
    return 'On Track';

  const isCurrentMonth = year === today.getFullYear() && month === today.getMonth() + 1;
  const elapsedDays = isCurrentMonth ? today.getDate() : totalDays;
  const elapsedFraction = elapsedDays / totalDays;

  const expectedCompleted = target * elapsedFraction;
  if (expectedCompleted === 0) return 'On Track';

  const paceRatio = completed / expectedCompleted;
  if (paceRatio >= 0.8) return 'On Track';
  if (paceRatio >= 0.5) return 'At Risk';
  return 'Behind';
};

const getInitials = (name = '') =>
  name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();

const AVATAR_COLORS = [
  '#4A6FA5', '#7B68EE', '#5F7A61', '#C07850',
  '#6B5B95', '#88B04B', '#D65C5C', '#4BACC6',
];

const avatarColor = (id) => AVATAR_COLORS[id % AVATAR_COLORS.length];

const groupByEmployee = (allocations, month, year) => {
  const map = {};
  allocations.forEach((a) => {
    if (!map[a.employeeId]) {
      map[a.employeeId] = {
        id: a.employeeId,
        name: a.employeeName,
        avatar: getInitials(a.employeeName),
        avatarColor: avatarColor(a.employeeId),
        assigned: 0,
        completed: 0,
        breakdown: [],
      };
    }
    const insp = map[a.employeeId];
    insp.assigned += a.targetCount;
    insp.completed += a.submissionCount;
    insp.breakdown.push({
      id: a.id,
      clientId: a.clientId,
      client: a.clientName || '—',
      siteId: a.siteId,
      site: a.siteName,
      assigned: a.targetCount,
      completed: a.submissionCount,
      progress: a.targetCount > 0 ? Math.round((a.submissionCount / a.targetCount) * 100) : 0,
    });
  });
  return Object.values(map).map((insp) => {
    const progress = insp.assigned > 0 ? Math.round((insp.completed / insp.assigned) * 100) : 0;
    return { ...insp, progress, status: computeStatus(insp.completed, insp.assigned, month, year) };
  });
};

const groupByClient = (allocations, month, year) => {
  const map = {};
  allocations.forEach((a) => {
    if (!map[a.clientId]) {
      map[a.clientId] = {
        id: a.clientId,
        name: a.clientName || '—',
        avatar: getInitials(a.clientName || '—'),
        avatarColor: avatarColor(a.clientId),
        assigned: 0,
        completed: 0,
        siteMap: {},
        inspectorMap: {},
        rawAllocations: [],
      };
    }
    const client = map[a.clientId];
    client.assigned += a.targetCount;
    client.completed += a.submissionCount;
    client.rawAllocations.push(a);

    if (!client.siteMap[a.siteId]) {
      client.siteMap[a.siteId] = { id: a.siteId, name: a.siteName, assigned: 0, completed: 0 };
    }
    client.siteMap[a.siteId].assigned += a.targetCount;
    client.siteMap[a.siteId].completed += a.submissionCount;

    if (!client.inspectorMap[a.employeeId]) {
      client.inspectorMap[a.employeeId] = {
        id: a.employeeId,
        name: a.employeeName,
        avatar: getInitials(a.employeeName),
        avatarColor: avatarColor(a.employeeId),
        assigned: 0,
        completed: 0,
      };
    }
    client.inspectorMap[a.employeeId].assigned += a.targetCount;
    client.inspectorMap[a.employeeId].completed += a.submissionCount;
  });

  return Object.values(map).map((client) => {
    const progress = client.assigned > 0 ? Math.round((client.completed / client.assigned) * 100) : 0;
    const sites = Object.values(client.siteMap).map((s) => ({
      ...s,
      progress: s.assigned > 0 ? Math.round((s.completed / s.assigned) * 100) : 0,
    }));
    const inspectors = Object.values(client.inspectorMap).map((i) => ({
      ...i,
      progress: i.assigned > 0 ? Math.round((i.completed / i.assigned) * 100) : 0,
    }));
    return {
      id: client.id,
      name: client.name,
      avatar: client.avatar,
      avatarColor: client.avatarColor,
      assigned: client.assigned,
      completed: client.completed,
      progress,
      status: computeStatus(client.completed, client.assigned, month, year),
      sites,
      inspectors,
      rawAllocations: client.rawAllocations,
    };
  });
};

// ─── Styled Components ────────────────────────────────────────────────────────

const PageWrapper = styled(Box)`
  padding: 24px 32px;
  background-color: #f5f6fa;
  min-height: 100vh;
`;

const StatCard = styled(Card)`
  border-radius: 16px !important;
  box-shadow: 0 2px 8px rgba(0,0,0,0.07) !important;
  height: 100%;
`;

const StatIconBox = styled(Box)`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ bg }) => bg};
`;

const PerformanceTable = styled(Card)`
  border-radius: 16px !important;
  box-shadow: 0 2px 8px rgba(0,0,0,0.07) !important;
  overflow: hidden !important;
`;

const TableHeaderRow = styled(Box)`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 2fr 1fr 40px;
  align-items: center;
  padding: 0 16px;
  height: 44px;
  background: #fff;
  border-bottom: 1px solid #f0f0f0;
`;

const TableDataRow = styled(Box)`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 2fr 1fr 40px;
  align-items: center;
  padding: 0 16px;
  height: 64px;
  cursor: pointer;
  border-left: 4px solid ${({ selected }) => selected ? '#F59300' : 'transparent'};
  background-color: ${({ selected }) => selected ? '#FFF8EE' : '#fff'};
  transition: background-color 0.15s;
  &:hover {
    background-color: ${({ selected }) => selected ? '#FFF8EE' : '#fafafa'};
  }
  &:not(:last-child) {
    border-bottom: 1px solid #f5f5f5;
  }
`;

const DetailPanel = styled(Card)`
  border-radius: 16px !important;
  box-shadow: 0 2px 8px rgba(0,0,0,0.07) !important;
  height: fit-content;
  position: sticky;
  top: 24px;
`;

const StyledProgress = styled(LinearProgress)`
  height: 8px !important;
  border-radius: 4px !important;
  background-color: #eee !important;
  .MuiLinearProgress-bar {
    border-radius: 4px !important;
    background-color: ${({ barcolor }) => barcolor} !important;
  }
`;

const CounterButton = styled(IconButton)`
  width: 28px !important;
  height: 28px !important;
  border: 1px solid #ddd !important;
  border-radius: 6px !important;
  padding: 0 !important;
`;

const TabButton = styled(Button)`
  border-radius: 20px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  padding: 5px 18px !important;
  min-width: 100px !important;
  box-shadow: none !important;
  ${({ active }) => active
    ? `background-color: #F59300 !important; color: #fff !important;`
    : `background-color: #f0f0f0 !important; color: #555 !important;`
  }
  &:hover {
    ${({ active }) => active
      ? `background-color: #d97e00 !important;`
      : `background-color: #e0e0e0 !important;`
    }
  }
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getStatusColor = (status) => {
  if (status === 'On Track') return { bg: '#E8F5E9', text: '#2E7D32' };
  if (status === 'At Risk') return { bg: '#FFF3E0', text: '#E65100' };
  return { bg: '#FFEBEE', text: '#C62828' };
};

const getProgressColor = (progress) => {
  if (progress >= 80) return '#4CAF50';
  if (progress >= 60) return '#F59300';
  return '#F44336';
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const StatCardItem = ({ icon, iconBg, value, label, sub, loading }) => (
  <StatCard>
    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
      <Box display="flex" alignItems="center" gap={2}>
        <StatIconBox bg={iconBg}>{icon}</StatIconBox>
        <Box>
          {loading
            ? <CircularProgress size={20} sx={{ color: '#F59300', mb: 0.5 }} />
            : <Typography variant="h4" fontWeight={700} lineHeight={1.1}>{value}</Typography>
          }
          <Typography variant="body2" fontWeight={600} color="text.primary" mt={0.3}>{label}</Typography>
          <Typography variant="caption" color="text.secondary">{sub}</Typography>
        </Box>
      </Box>
    </CardContent>
  </StatCard>
);

const StatusChip = ({ status }) => {
  const colors = getStatusColor(status);
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', px: 1.5, py: 0.4,
      borderRadius: '20px', bgcolor: colors.bg,
      color: colors.text, fontWeight: 600, fontSize: '12px', whiteSpace: 'nowrap'
    }}>
      {status}
    </Box>
  );
};

// ─── Inspector Detail Panel ───────────────────────────────────────────────────

const InspectorDetail = ({ inspector, clients, allSites, userName, month, year, onClose, onSaved }) => {
  const [assignments, setAssignments] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setAssignments(
      inspector.breakdown.map((b) => ({
        id: b.id,
        clientId: b.clientId || '',
        siteId: b.siteId,
        site: b.site,
        count: b.assigned,
      }))
    );
  }, [inspector]);

  const sitesForClient = (clientId) =>
    allSites.filter((s) => s.clientId === clientId || s.clientId === parseInt(clientId));

  const handleCountChange = (index, delta) => {
    setAssignments((prev) =>
      prev.map((a, i) => i === index ? { ...a, count: Math.max(0, a.count + delta) } : a)
    );
  };

  const handleClientChange = (index, clientId) => {
    const sites = sitesForClient(clientId);
    setAssignments((prev) =>
      prev.map((a, i) =>
        i === index ? { ...a, clientId, siteId: sites[0]?.id || '', site: sites[0]?.siteName || '' } : a
      )
    );
  };

  const handleSiteChange = (index, siteId) => {
    const site = allSites.find((s) => s.id === siteId || s.id === parseInt(siteId));
    setAssignments((prev) =>
      prev.map((a, i) => i === index ? { ...a, siteId, site: site?.siteName || '' } : a)
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      for (const a of assignments) {
        if (!a.siteId) continue;
        if (a.count === 0 && a.id) {
          await deleteSiteAssignment(a.id);
        } else {
          await addSiteAssignment({
            id: a.id || 0,
            employeeId: inspector.id,
            employeeName: inspector.name,
            siteId: a.siteId,
            siteName: a.site,
            targetCount: a.count,
            month,
            year,
            isActive: true,
            userName,
          });
        }
      }
      onSaved('Changes saved successfully.');
    } catch {
      onSaved('Error saving changes. Please try again.', true);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setAssignments(
      inspector.breakdown.map((b) => ({
        id: b.id,
        clientId: b.clientId || '',
        siteId: b.siteId,
        site: b.site,
        count: b.assigned,
      }))
    );
  };

  return (
    <DetailPanel>
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box display="flex" gap={2} alignItems="center">
            <Avatar sx={{ width: 56, height: 56, bgcolor: inspector.avatarColor, fontSize: 18, fontWeight: 700 }}>
              {inspector.avatar}
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700}>{inspector.name}</Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
        </Box>

        <Box display="flex" gap={2} mb={2.5}>
          {[
            { label: 'Assigned', value: inspector.assigned },
            { label: 'Completed', value: inspector.completed },
            { label: 'Remaining', value: inspector.assigned - inspector.completed },
          ].map(({ label, value }) => (
            <Box key={label} flex={1} textAlign="center" sx={{ bgcolor: '#f8f9fa', borderRadius: 2, py: 1.5 }}>
              <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
              <Typography variant="h6" fontWeight={700}>{value}</Typography>
            </Box>
          ))}
        </Box>

        <Typography variant="body2" fontWeight={600} mb={1}>Overall Progress</Typography>
        <Box display="flex" alignItems="center" gap={1.5} mb={0.5}>
          <Box flex={1}>
            <StyledProgress variant="determinate" value={inspector.progress} barcolor={getProgressColor(inspector.progress)} />
          </Box>
          <Typography variant="body2" fontWeight={700}>{inspector.progress}%</Typography>
          <StatusChip status={inspector.status} />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="body2" fontWeight={600} mb={1.5}>Breakdown by Client / Site</Typography>
        <Box display="flex" mb={1} px={0.5}>
          <Typography variant="caption" color="text.secondary" flex={1}>CLIENT / SITE</Typography>
          <Typography variant="caption" color="text.secondary" width={64} textAlign="right">ASSIGNED</Typography>
          <Typography variant="caption" color="text.secondary" width={72} textAlign="right">COMPLETED</Typography>
          <Typography variant="caption" color="text.secondary" width={64} textAlign="right">PROGRESS</Typography>
        </Box>
        {inspector.breakdown.map((row, i) => (
          <Box key={i} mb={1.5}>
            <Box display="flex" alignItems="flex-start" mb={0.5}>
              <Box flex={1}>
                <Typography variant="body2" fontWeight={600} fontSize={13}>{row.client}</Typography>
                <Box display="flex" alignItems="center" gap={0.3}>
                  <LocationOnIcon sx={{ fontSize: 11, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">{row.site}</Typography>
                </Box>
              </Box>
              <Typography variant="body2" fontWeight={600} width={64} textAlign="right">{row.assigned}</Typography>
              <Typography variant="body2" fontWeight={600} width={72} textAlign="right">{row.completed}</Typography>
              <Typography variant="body2" fontWeight={600} width={64} textAlign="right" color={getProgressColor(row.progress)}>{row.progress}%</Typography>
            </Box>
            <StyledProgress variant="determinate" value={row.progress} barcolor={getProgressColor(row.progress)} />
          </Box>
        ))}

        <Divider sx={{ my: 2 }} />

        <Typography variant="body2" fontWeight={600} mb={1.5}>Adjust Assignments</Typography>
        {assignments.map((a, i) => (
          <Box key={i} mb={1.5}>
            <Box display="flex" gap={1} mb={1}>
              <Box flex={1}>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Client</Typography>
                <FormControl fullWidth size="small">
                  <Select value={a.clientId || ''} onChange={(e) => handleClientChange(i, e.target.value)}
                    sx={{ fontSize: 13, borderRadius: 2 }}>
                    {clients.map((c) => (
                      <MenuItem key={c.id} value={c.id} sx={{ fontSize: 13 }}>{c.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box flex={1}>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Site</Typography>
                <FormControl fullWidth size="small">
                  <Select value={a.siteId || ''} onChange={(e) => handleSiteChange(i, e.target.value)}
                    sx={{ fontSize: 13, borderRadius: 2 }}>
                    {allSites.filter((s) => s.clientId === a.clientId || s.clientId === parseInt(a.clientId)).map((s) => (
                      <MenuItem key={s.id} value={s.id} sx={{ fontSize: 13 }}>{s.siteName}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="caption" color="text.secondary">Target Count</Typography>
              <Box display="flex" alignItems="center" gap={1} ml={1}>
                <CounterButton onClick={() => handleCountChange(i, -1)}><RemoveIcon sx={{ fontSize: 14 }} /></CounterButton>
                <Typography variant="body2" fontWeight={700} minWidth={24} textAlign="center">{a.count}</Typography>
                <CounterButton onClick={() => handleCountChange(i, 1)}><AddIcon sx={{ fontSize: 14 }} /></CounterButton>
              </Box>
            </Box>
          </Box>
        ))}

        <Box display="flex" gap={1.5} mt={2.5}>
          <Button fullWidth variant="outlined" onClick={handleReset}
            sx={{ borderColor: '#ddd', color: '#555', borderRadius: 2, textTransform: 'none', fontWeight: 600,
              '&:hover': { borderColor: '#bbb', bgcolor: '#f5f5f5' } }}>
            Reset
          </Button>
          <Button fullWidth variant="contained" onClick={handleSave} disabled={saving}
            sx={{ bgcolor: '#F59300', borderRadius: 2, textTransform: 'none', fontWeight: 600, boxShadow: 'none',
              '&:hover': { bgcolor: '#d97e00', boxShadow: 'none' } }}>
            {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save Changes'}
          </Button>
        </Box>
      </CardContent>
    </DetailPanel>
  );
};

// ─── Client Detail Panel ──────────────────────────────────────────────────────

const ClientDetail = ({ client, allEmployees, userName, month, year, onClose, onSaved }) => {
  const [assignments, setAssignments] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setAssignments(
      client.rawAllocations.map((a) => ({
        id: a.id,
        siteId: a.siteId,
        site: a.siteName,
        employeeId: a.employeeId,
        employeeName: a.employeeName,
        count: a.targetCount,
      }))
    );
  }, [client]);

  const handleCountChange = (index, delta) => {
    setAssignments((prev) =>
      prev.map((a, i) => i === index ? { ...a, count: Math.max(0, a.count + delta) } : a)
    );
  };

  const handleSiteChange = (index, siteId) => {
    const site = client.sites.find((s) => s.id === siteId || s.id === parseInt(siteId));
    setAssignments((prev) =>
      prev.map((a, i) => i === index ? { ...a, siteId, site: site?.name || '' } : a)
    );
  };

  const handleInspectorChange = (index, employeeId) => {
    const emp = allEmployees.find((e) => e.id === employeeId || e.id === parseInt(employeeId));
    setAssignments((prev) =>
      prev.map((a, i) => i === index ? { ...a, employeeId, employeeName: emp?.name || '' } : a)
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      for (const a of assignments) {
        if (!a.siteId || !a.employeeId) continue;
        if (a.count === 0 && a.id) {
          await deleteSiteAssignment(a.id);
        } else {
          await addSiteAssignment({
            id: a.id || 0,
            employeeId: a.employeeId,
            employeeName: a.employeeName,
            siteId: a.siteId,
            siteName: a.site,
            targetCount: a.count,
            month,
            year,
            isActive: true,
            userName,
          });
        }
      }
      onSaved('Changes saved successfully.');
    } catch {
      onSaved('Error saving changes. Please try again.', true);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setAssignments(
      client.rawAllocations.map((a) => ({
        id: a.id,
        siteId: a.siteId,
        site: a.siteName,
        employeeId: a.employeeId,
        employeeName: a.employeeName,
        count: a.targetCount,
      }))
    );
  };

  return (
    <DetailPanel>
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box display="flex" gap={2} alignItems="center">
            <Avatar sx={{ width: 56, height: 56, bgcolor: client.avatarColor, fontSize: 18, fontWeight: 700 }}>
              {client.avatar}
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700}>{client.name}</Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
        </Box>

        {/* Stats */}
        <Box display="flex" gap={2} mb={2.5}>
          {[
            { label: 'Assigned', value: client.assigned },
            { label: 'Completed', value: client.completed },
            { label: 'Remaining', value: client.assigned - client.completed },
          ].map(({ label, value }) => (
            <Box key={label} flex={1} textAlign="center" sx={{ bgcolor: '#f8f9fa', borderRadius: 2, py: 1.5 }}>
              <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
              <Typography variant="h6" fontWeight={700}>{value}</Typography>
            </Box>
          ))}
        </Box>

        {/* Overall Progress */}
        <Typography variant="body2" fontWeight={600} mb={1}>Overall Progress</Typography>
        <Box display="flex" alignItems="center" gap={1.5} mb={0.5}>
          <Box flex={1}>
            <StyledProgress variant="determinate" value={client.progress} barcolor={getProgressColor(client.progress)} />
          </Box>
          <Typography variant="body2" fontWeight={700}>{client.progress}%</Typography>
          <StatusChip status={client.status} />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Breakdown by Site */}
        <Typography variant="body2" fontWeight={600} mb={1.5}>Breakdown by Site</Typography>
        <Box display="flex" mb={1} px={0.5}>
          <Typography variant="caption" color="text.secondary" flex={1}>SITE</Typography>
          <Typography variant="caption" color="text.secondary" width={64} textAlign="right">ASSIGNED</Typography>
          <Typography variant="caption" color="text.secondary" width={72} textAlign="right">COMPLETED</Typography>
          <Typography variant="caption" color="text.secondary" width={64} textAlign="right">PROGRESS</Typography>
        </Box>
        {client.sites.map((site, i) => (
          <Box key={i} mb={1.5}>
            <Box display="flex" alignItems="center" mb={0.5}>
              <Box flex={1} display="flex" alignItems="center" gap={0.5}>
                <LocationOnIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                <Typography variant="body2" fontWeight={600} fontSize={13}>{site.name}</Typography>
              </Box>
              <Typography variant="body2" fontWeight={600} width={64} textAlign="right">{site.assigned}</Typography>
              <Typography variant="body2" fontWeight={600} width={72} textAlign="right">{site.completed}</Typography>
              <Typography variant="body2" fontWeight={600} width={64} textAlign="right" color={getProgressColor(site.progress)}>{site.progress}%</Typography>
            </Box>
            <StyledProgress variant="determinate" value={site.progress} barcolor={getProgressColor(site.progress)} />
          </Box>
        ))}

        <Divider sx={{ my: 2 }} />

        {/* Inspectors Working This Client */}
        <Typography variant="body2" fontWeight={600} mb={1.5}>Inspectors Working This Client</Typography>
        <Box display="flex" mb={1} px={0.5}>
          <Typography variant="caption" color="text.secondary" flex={1}>INSPECTOR</Typography>
          <Typography variant="caption" color="text.secondary" width={64} textAlign="right">ASSIGNED</Typography>
          <Typography variant="caption" color="text.secondary" width={72} textAlign="right">COMPLETED</Typography>
          <Typography variant="caption" color="text.secondary" width={64} textAlign="right">PROGRESS</Typography>
        </Box>
        {client.inspectors.map((insp, i) => (
          <Box key={i} mb={1.5}>
            <Box display="flex" alignItems="center" mb={0.5}>
              <Box flex={1} display="flex" alignItems="center" gap={1}>
                <Avatar sx={{ width: 24, height: 24, bgcolor: insp.avatarColor, fontSize: 10, fontWeight: 700 }}>
                  {insp.avatar}
                </Avatar>
                <Typography variant="body2" fontWeight={600} fontSize={13}>{insp.name}</Typography>
              </Box>
              <Typography variant="body2" fontWeight={600} width={64} textAlign="right">{insp.assigned}</Typography>
              <Typography variant="body2" fontWeight={600} width={72} textAlign="right">{insp.completed}</Typography>
              <Typography variant="body2" fontWeight={600} width={64} textAlign="right" color={getProgressColor(insp.progress)}>{insp.progress}%</Typography>
            </Box>
            <StyledProgress variant="determinate" value={insp.progress} barcolor={getProgressColor(insp.progress)} />
          </Box>
        ))}

        <Divider sx={{ my: 2 }} />

        {/* Adjust Assignments */}
        <Typography variant="body2" fontWeight={600} mb={1.5}>Adjust Assignments</Typography>
        {assignments.map((a, i) => (
          <Box key={i} mb={1.5}>
            <Box display="flex" gap={1} mb={1}>
              <Box flex={1}>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Site</Typography>
                <FormControl fullWidth size="small">
                  <Select value={a.siteId || ''} onChange={(e) => handleSiteChange(i, e.target.value)}
                    sx={{ fontSize: 13, borderRadius: 2 }}>
                    {client.sites.map((s) => (
                      <MenuItem key={s.id} value={s.id} sx={{ fontSize: 13 }}>{s.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box flex={1}>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Inspector</Typography>
                <FormControl fullWidth size="small">
                  <Select value={a.employeeId || ''} onChange={(e) => handleInspectorChange(i, e.target.value)}
                    sx={{ fontSize: 13, borderRadius: 2 }}>
                    {allEmployees.map((emp) => (
                      <MenuItem key={emp.id} value={emp.id} sx={{ fontSize: 13 }}>{emp.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="caption" color="text.secondary">Target Count</Typography>
              <Box display="flex" alignItems="center" gap={1} ml={1}>
                <CounterButton onClick={() => handleCountChange(i, -1)}><RemoveIcon sx={{ fontSize: 14 }} /></CounterButton>
                <Typography variant="body2" fontWeight={700} minWidth={24} textAlign="center">{a.count}</Typography>
                <CounterButton onClick={() => handleCountChange(i, 1)}><AddIcon sx={{ fontSize: 14 }} /></CounterButton>
              </Box>
            </Box>
          </Box>
        ))}

        {/* Actions */}
        <Box display="flex" gap={1.5} mt={2.5}>
          <Button fullWidth variant="outlined" onClick={handleReset}
            sx={{ borderColor: '#ddd', color: '#555', borderRadius: 2, textTransform: 'none', fontWeight: 600,
              '&:hover': { borderColor: '#bbb', bgcolor: '#f5f5f5' } }}>
            Reset
          </Button>
          <Button fullWidth variant="contained" onClick={handleSave} disabled={saving}
            sx={{ bgcolor: '#F59300', borderRadius: 2, textTransform: 'none', fontWeight: 600, boxShadow: 'none',
              '&:hover': { bgcolor: '#d97e00', boxShadow: 'none' } }}>
            {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save Changes'}
          </Button>
        </Box>
      </CardContent>
    </DetailPanel>
  );
};

// ─── Add Assignment Modal ─────────────────────────────────────────────────────

const buildMonthYearOptions = () => {
  const opts = [];
  for (const year of YEAR_OPTIONS) {
    for (const m of MONTH_OPTIONS) {
      opts.push({ value: `${m.value}-${year}`, label: `${m.label} ${year}`, month: m.value, year });
    }
  }
  return opts;
};

const MONTH_YEAR_OPTIONS = buildMonthYearOptions();

const AddAssignmentModal = ({ open, onClose, mode, defaultMonth, defaultYear, clients, allSites, allEmployees, allocations, userName, onSaved }) => {
  const [selectedMonthYear, setSelectedMonthYear] = useState(`${defaultMonth}-${defaultYear}`);
  const [inspectorId, setInspectorId] = useState('');
  const [clientId, setClientId] = useState('');
  const [siteId, setSiteId] = useState('');
  const [targetCount, setTargetCount] = useState(1);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedMonthYear(`${defaultMonth}-${defaultYear}`);
      setInspectorId('');
      setClientId('');
      setSiteId('');
      setTargetCount(1);
    }
  }, [open, defaultMonth, defaultYear]);

  const filteredSites = allSites.filter(
    (s) => s.clientId === clientId || s.clientId === parseInt(clientId)
  );

  const handleClientChange = (val) => {
    setClientId(val);
    setSiteId('');
  };

  const existing = allocations.find(
    (a) => String(a.employeeId) === String(inspectorId) && String(a.siteId) === String(siteId)
  );
  const isValid = inspectorId && siteId && (targetCount >= 1 || (targetCount === 0 && existing));

  const handleSubmit = async () => {
    if (!isValid) return;
    setSaving(true);
    const parsed = MONTH_YEAR_OPTIONS.find((o) => o.value === selectedMonthYear);
    const employee = allEmployees.find((e) => e.id === inspectorId || e.id === parseInt(inspectorId));
    const site = allSites.find((s) => s.id === siteId || s.id === parseInt(siteId));
    try {
      if (targetCount === 0 && existing) {
        await deleteSiteAssignment(existing.id);
        onSaved('Assignment removed.');
      } else {
        await addSiteAssignment({
          id: existing?.id || 0,
          employeeId: inspectorId,
          employeeName: employee?.name || '',
          siteId,
          siteName: site?.siteName || '',
          targetCount,
          month: parsed?.month,
          year: parsed?.year,
          isActive: true,
          userName,
        });
        onSaved(existing ? 'Assignment updated successfully.' : 'Assignment created successfully.');
      }
      onClose();
    } catch {
      onSaved('Error saving assignment. Please try again.', true);
    } finally {
      setSaving(false);
    }
  };

  const fieldLabelSx = { variant: 'caption', color: 'text.secondary', display: 'block', mb: 0.5, fontWeight: 600 };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 0 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="h6" fontWeight={700}>Add Assignment</Typography>
            <Typography variant="body2" color="text.secondary" mt={0.3}>
              {mode === 'inspector'
                ? 'Assign inspection targets to an inspector.'
                : 'Assign inspection targets for a client site.'}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ mt: -0.5 }}><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2.5 }}>
        {/* Month */}
        <Box mb={2.5}>
          <Typography {...fieldLabelSx}>Month</Typography>
          <FormControl fullWidth size="small">
            <Select
              value={selectedMonthYear}
              onChange={(e) => setSelectedMonthYear(e.target.value)}
              startAdornment={<CalendarTodayIcon sx={{ fontSize: 15, mr: 1, color: 'text.secondary' }} />}
              sx={{ borderRadius: 2 }}
            >
              {MONTH_YEAR_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {mode === 'inspector' ? (
          <>
            {/* Inspector */}
            <Box mb={2.5}>
              <Typography {...fieldLabelSx}>Inspector</Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={inspectorId}
                  onChange={(e) => setInspectorId(e.target.value)}
                  displayEmpty
                  startAdornment={<PersonOutlineIcon sx={{ fontSize: 15, mr: 1, color: 'text.secondary' }} />}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="" disabled>Select Inspector</MenuItem>
                  {allEmployees.map((emp) => (
                    <MenuItem key={emp.id} value={emp.id}>{emp.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            {/* Client */}
            <Box mb={2.5}>
              <Typography {...fieldLabelSx}>Client</Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={clientId}
                  onChange={(e) => handleClientChange(e.target.value)}
                  displayEmpty
                  startAdornment={<BusinessIcon sx={{ fontSize: 15, mr: 1, color: 'text.secondary' }} />}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="" disabled>Select Client</MenuItem>
                  {clients.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            {/* Site */}
            <Box mb={2.5}>
              <Typography {...fieldLabelSx}>Site</Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={siteId}
                  onChange={(e) => setSiteId(e.target.value)}
                  displayEmpty
                  disabled={!clientId}
                  startAdornment={<LocationOnIcon sx={{ fontSize: 15, mr: 1, color: 'text.secondary' }} />}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="" disabled>Select Site</MenuItem>
                  {filteredSites.map((s) => (
                    <MenuItem key={s.id} value={s.id}>{s.siteName}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </>
        ) : (
          <>
            {/* Client */}
            <Box mb={2.5}>
              <Typography {...fieldLabelSx}>Client</Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={clientId}
                  onChange={(e) => handleClientChange(e.target.value)}
                  displayEmpty
                  startAdornment={<BusinessIcon sx={{ fontSize: 15, mr: 1, color: 'text.secondary' }} />}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="" disabled>Select Client</MenuItem>
                  {clients.map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            {/* Site */}
            <Box mb={2.5}>
              <Typography {...fieldLabelSx}>Site</Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={siteId}
                  onChange={(e) => setSiteId(e.target.value)}
                  displayEmpty
                  disabled={!clientId}
                  startAdornment={<LocationOnIcon sx={{ fontSize: 15, mr: 1, color: 'text.secondary' }} />}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="" disabled>Select Site</MenuItem>
                  {filteredSites.map((s) => (
                    <MenuItem key={s.id} value={s.id}>{s.siteName}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            {/* Inspector */}
            <Box mb={2.5}>
              <Typography {...fieldLabelSx}>Inspector</Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={inspectorId}
                  onChange={(e) => setInspectorId(e.target.value)}
                  displayEmpty
                  startAdornment={<PersonOutlineIcon sx={{ fontSize: 15, mr: 1, color: 'text.secondary' }} />}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="" disabled>Select Inspector</MenuItem>
                  {allEmployees.map((emp) => (
                    <MenuItem key={emp.id} value={emp.id}>{emp.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </>
        )}

        {/* Target Count */}
        <Box mb={3}>
          <Typography {...fieldLabelSx}>Target Count</Typography>
          <Box display="flex" alignItems="center" gap={1.5}>
            <CounterButton onClick={() => setTargetCount((p) => Math.max(0, p - 1))}>
              <RemoveIcon sx={{ fontSize: 14 }} />
            </CounterButton>
            <Typography variant="body1" fontWeight={700} minWidth={28} textAlign="center">{targetCount}</Typography>
            <CounterButton onClick={() => setTargetCount((p) => p + 1)}>
              <AddIcon sx={{ fontSize: 14 }} />
            </CounterButton>
          </Box>
        </Box>

        {/* Actions */}
        <Box display="flex" gap={1.5}>
          <Button fullWidth variant="outlined" onClick={onClose}
            sx={{ borderColor: '#ddd', color: '#555', borderRadius: 2, textTransform: 'none', fontWeight: 600,
              '&:hover': { borderColor: '#bbb', bgcolor: '#f5f5f5' } }}>
            Cancel
          </Button>
          <Button fullWidth variant="contained" onClick={handleSubmit} disabled={saving || !isValid}
            sx={{ bgcolor: '#F59300', borderRadius: 2, textTransform: 'none', fontWeight: 600, boxShadow: 'none',
              '&:hover': { bgcolor: '#d97e00', boxShadow: 'none' } }}>
            {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save Assignment'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const Assignments = () => {
  const [selectedMonth, setSelectedMonth] = useState(DEFAULT_MONTH);
  const [selectedYear, setSelectedYear] = useState(DEFAULT_YEAR);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('clients');
  const [allocations, setAllocations] = useState([]);
  const [selectedInspector, setSelectedInspector] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [allSites, setAllSites] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [page, setPage] = useState(1);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const rowsPerPage = 6;

  useEffect(() => {
    try {
      const profile = JSON.parse(localStorage.getItem('userProfile') || '{}');
      setUserName(profile.userName || '');
      setUserEmail(profile.userEmail || '');
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (!userEmail) return;
    getCompany(userEmail)
      .then((res) => { if (res.data.successCode === 0) setClients(res.data.data || []); })
      .catch(() => {});
    getSiteData(userEmail)
      .then((res) => { if (res.data.successCode === 0) setAllSites(res.data.data || []); })
      .catch(() => {});
    getEmployee()
      .then((res) => { if (res.data.successCode === 0) setAllEmployees(res.data.data || []); })
      .catch(() => {});
  }, [userEmail]);

  const loadAllocations = useCallback(() => {
    setLoading(true);
    getSiteAssignmentList(selectedYear, selectedMonth)
      .then((res) => {
        if (res.data.successCode === 0) {
          const data = res.data.data || [];
          setAllocations(data);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedYear, selectedMonth]);

  useEffect(() => {
    loadAllocations();
  }, [loadAllocations]);

  // Recompute grouped rows from raw allocations
  const inspectorRows = groupByEmployee(allocations, selectedMonth, selectedYear);
  const clientRows = groupByClient(allocations, selectedMonth, selectedYear);

  // Keep selected items in sync after reload
  useEffect(() => {
    setSelectedInspector((prev) => prev ? (inspectorRows.find((r) => r.id === prev.id) || null) : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allocations]);
  useEffect(() => {
    setSelectedClient((prev) => prev ? (clientRows.find((r) => r.id === prev.id) || null) : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allocations]);

  // Summary stats
  const totalAssigned = inspectorRows.reduce((s, i) => s + i.assigned, 0);
  const totalCompleted = inspectorRows.reduce((s, i) => s + i.completed, 0);
  const totalRemaining = totalAssigned - totalCompleted;
  const overallPct = totalAssigned > 0 ? Math.round((totalCompleted / totalAssigned) * 100) : 0;
  const needsAttention = clientRows.filter((c) => c.status === 'Behind').length;

  const activeRows = activeTab === 'inspectors' ? inspectorRows : clientRows;
  const filtered = activeRows.filter((r) => r.name.toLowerCase().includes(search.toLowerCase()));
  const paginated = filtered.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearch('');
    setPage(1);
    setSelectedInspector(null);
    setSelectedClient(null);
  };

  const handleMonthChange = (e) => {
    setSelectedMonth(Number(e.target.value));
    setPage(1);
    setSelectedInspector(null);
    setSelectedClient(null);
  };

  const handleYearChange = (e) => {
    setSelectedYear(Number(e.target.value));
    setPage(1);
    setSelectedInspector(null);
    setSelectedClient(null);
  };

  const handleSaved = (message, isError = false) => {
    toast(message, { progressStyle: { background: '#F59300' } });
    if (!isError) loadAllocations();
  };

  const selectedDetail = activeTab === 'inspectors' ? selectedInspector : selectedClient;
  const hasDetail = Boolean(selectedDetail);

  return (
    <PageWrapper>
      {/* Page Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700} mb={0.5}>Assignments</Typography>
          <Typography variant="body2" color="text.secondary">
            Plan inspection targets and monitor performance across inspectors, clients, and sites.
          </Typography>
        </Box>
        <Box display="flex" gap={1.5} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <Select
              value={selectedMonth}
              onChange={handleMonthChange}
              startAdornment={<CalendarTodayIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />}
              sx={{ borderRadius: 2, fontSize: 14, fontWeight: 600, bgcolor: '#fff' }}
            >
              {MONTH_OPTIONS.map((m) => (
                <MenuItem key={m.value} value={m.value} sx={{ fontSize: 14 }}>{m.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 90 }}>
            <Select
              value={selectedYear}
              onChange={handleYearChange}
              sx={{ borderRadius: 2, fontSize: 14, fontWeight: 600, bgcolor: '#fff' }}
            >
              {YEAR_OPTIONS.map((y) => (
                <MenuItem key={y} value={y} sx={{ fontSize: 14 }}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddModalOpen(true)}
            sx={{ bgcolor: '#F59300', borderRadius: 2, textTransform: 'none', fontWeight: 600,
              boxShadow: 'none', whiteSpace: 'nowrap',
              '&:hover': { bgcolor: '#d97e00', boxShadow: 'none' } }}
          >
            Add Assignment
          </Button>
        </Box>
      </Box>

      {/* Summary Stats */}
      <Grid container spacing={2.5} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCardItem
            icon={<AssignmentIcon sx={{ color: '#F59300', fontSize: 26 }} />}
            iconBg="#FFF3E0"
            value={totalAssigned}
            label="Total Assigned"
            sub="Across all clients"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCardItem
            icon={<CheckCircleOutlineIcon sx={{ color: '#4CAF50', fontSize: 26 }} />}
            iconBg="#E8F5E9"
            value={totalCompleted}
            label="Completed"
            sub={`${overallPct}% completion`}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCardItem
            icon={<AccessTimeIcon sx={{ color: '#2196F3', fontSize: 26 }} />}
            iconBg="#E3F2FD"
            value={totalRemaining}
            label="Remaining"
            sub="Still due"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCardItem
            icon={<WarningAmberIcon sx={{ color: '#D32F2F', fontSize: 26 }} />}
            iconBg="#FFEBEE"
            value={needsAttention}
            label="Needs Attention"
            sub="Clients behind target"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Main Content */}
      <Grid container spacing={2.5}>
        {/* Performance Table */}
        <Grid item xs={12} md={hasDetail ? 7 : 12}>
          <PerformanceTable>
            {/* Table toolbar */}
            <Box sx={{ p: 2.5, pb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="subtitle1" fontWeight={700} mr={1}>Assignment Performance</Typography>
                <TabButton active={activeTab === 'clients' ? 1 : 0} onClick={() => handleTabChange('clients')} variant="contained">
                  Clients
                </TabButton>
                <TabButton active={activeTab === 'inspectors' ? 1 : 0} onClick={() => handleTabChange('inspectors')} variant="contained">
                  Inspectors
                </TabButton>
              </Box>
              <TextField
                size="small"
                placeholder={activeTab === 'inspectors' ? 'Search inspector...' : 'Search clients...'}
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>,
                  sx: { borderRadius: 2, fontSize: 13, bgcolor: '#f8f9fa' }
                }}
                sx={{ width: 220 }}
              />
            </Box>

            <TableHeaderRow>
              <Typography variant="caption" fontWeight={700} color="text.secondary" letterSpacing={0.5}>
                {activeTab === 'inspectors' ? 'INSPECTOR' : 'CLIENT'}
              </Typography>
              <Typography variant="caption" fontWeight={700} color="text.secondary" letterSpacing={0.5} textAlign="center">ASSIGNED</Typography>
              <Typography variant="caption" fontWeight={700} color="text.secondary" letterSpacing={0.5} textAlign="center">COMPLETED</Typography>
              <Typography variant="caption" fontWeight={700} color="text.secondary" letterSpacing={0.5}>PROGRESS</Typography>
              <Typography variant="caption" fontWeight={700} color="text.secondary" letterSpacing={0.5} textAlign="center">STATUS</Typography>
              <Box />
            </TableHeaderRow>

            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" height={200}>
                <CircularProgress sx={{ color: '#F59300' }} />
              </Box>
            ) : filtered.length === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" height={120}>
                <Typography variant="body2" color="text.secondary">
                  {activeTab === 'inspectors' ? 'No inspectors found.' : 'No clients found.'}
                </Typography>
              </Box>
            ) : (
              paginated.map((row) => {
                const isSelected = activeTab === 'inspectors'
                  ? selectedInspector?.id === row.id
                  : selectedClient?.id === row.id;
                return (
                  <TableDataRow
                    key={row.id}
                    selected={isSelected ? 1 : 0}
                    onClick={() => {
                      if (activeTab === 'inspectors') {
                        setSelectedInspector(row);
                        setSelectedClient(null);
                      } else {
                        setSelectedClient(row);
                        setSelectedInspector(null);
                      }
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Avatar sx={{ width: 38, height: 38, bgcolor: row.avatarColor, fontSize: 13, fontWeight: 700 }}>
                        {row.avatar}
                      </Avatar>
                      <Typography variant="body2" fontWeight={600}>{row.name}</Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={600} textAlign="center">{row.assigned}</Typography>
                    <Typography variant="body2" fontWeight={600} textAlign="center">{row.completed}</Typography>
                    <Box display="flex" alignItems="center" gap={1.5} pr={2}>
                      <Box flex={1}>
                        <StyledProgress variant="determinate" value={row.progress} barcolor={getProgressColor(row.progress)} />
                      </Box>
                      <Typography variant="caption" fontWeight={600} minWidth={28}>{row.progress}%</Typography>
                    </Box>
                    <Box textAlign="center">
                      <StatusChip status={row.status} />
                    </Box>
                    <Box display="flex" justifyContent="center">
                      <KeyboardArrowRightIcon sx={{ color: '#bbb', fontSize: 20 }} />
                    </Box>
                  </TableDataRow>
                );
              })
            )}

            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #f0f0f0' }}>
              <Typography variant="caption" color="text.secondary">
                {filtered.length === 0
                  ? 'No results'
                  : `Showing ${Math.min((page - 1) * rowsPerPage + 1, filtered.length)} to ${Math.min(page * rowsPerPage, filtered.length)} of ${filtered.length} ${activeTab === 'inspectors' ? 'inspectors' : 'clients'}`
                }
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <IconButton size="small" disabled={page === 1} onClick={() => setPage((p) => p - 1)}
                  sx={{ border: '1px solid #e0e0e0', borderRadius: 1.5, width: 30, height: 30 }}>
                  {'<'}
                </IconButton>
                <Box sx={{ width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: '#F59300', borderRadius: 1.5 }}>
                  <Typography variant="caption" fontWeight={700} color="white">{page}</Typography>
                </Box>
                <IconButton size="small" disabled={page * rowsPerPage >= filtered.length} onClick={() => setPage((p) => p + 1)}
                  sx={{ border: '1px solid #e0e0e0', borderRadius: 1.5, width: 30, height: 30 }}>
                  {'>'}
                </IconButton>
              </Box>
            </Box>
          </PerformanceTable>
        </Grid>

        {/* Detail Panel */}
        {selectedInspector && (
          <Grid item xs={12} md={5}>
            <InspectorDetail
              inspector={selectedInspector}
              clients={clients}
              allSites={allSites}
              userName={userName}
              month={selectedMonth}
              year={selectedYear}
              onClose={() => setSelectedInspector(null)}
              onSaved={handleSaved}
            />
          </Grid>
        )}
        {selectedClient && (
          <Grid item xs={12} md={5}>
            <ClientDetail
              client={selectedClient}
              allEmployees={allEmployees}
              userName={userName}
              month={selectedMonth}
              year={selectedYear}
              onClose={() => setSelectedClient(null)}
              onSaved={handleSaved}
            />
          </Grid>
        )}
      </Grid>

      <AddAssignmentModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        mode={activeTab === 'inspectors' ? 'inspector' : 'client'}
        defaultMonth={selectedMonth}
        defaultYear={selectedYear}
        clients={clients}
        allSites={allSites}
        allEmployees={allEmployees}
        allocations={allocations}
        userName={userName}
        onSaved={(msg, isError) => {
          toast(msg, { progressStyle: { background: '#F59300' } });
          if (!isError) loadAllocations();
        }}
      />

      <ToastContainer theme="dark" />
    </PageWrapper>
  );
};

export default Assignments;
